import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { accountKey } from './accountsFormat';

/*
 * AccountsSolicitationsTab — GAS's "closing soon" radar, account-scoped.
 *
 * Open Cal eProcure solicitations for this agency, soonest close first. Reads
 * /api/solicitations?dept= — the JWT endpoint TekStak's own Solicitations page
 * uses, which gained an agency filter for this tab.
 *
 * It deliberately does NOT read /api/v1/procurement/calendar, which is what GAS
 * reads: that route sits on the agent-tools surface behind a daily cap, so
 * pointing page traffic at it would spend the assistant's budget on page views.
 *
 * The filter matches on department_id OR the verbatim Cal eProcure name,
 * because ingest leaves ~19% of active rows without an id — including CDCR's
 * 1,128. Scoping on the id alone would show those accounts an empty list while
 * their solicitations sit right there.
 */

const CLOSING_LIMIT = 25;

function fmtDay(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - Date.now()) / 86400000);
}

export default function AccountsSolicitationsTab({ selectedDepartment }) {
  const account = accountKey(selectedDepartment);

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts-solicitations', account],
    queryFn: () => apiClient.get('/api/solicitations', {
      params: { dept: account, limit: 200 },
    }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  if (!account) {
    return <p className="accounts-panel-note">Select an agency to see its solicitations.</p>;
  }
  if (isLoading) {
    return <div className="accounts-panel-note">Loading solicitations…</div>;
  }
  if (error) {
    return (
      <div className="accounts-error">
        <AlertCircle size={16} /> {error.message || 'Failed to load solicitations'}
      </div>
    );
  }

  const rows = data?.rows || [];
  // Still-open first, soonest close leading — the radar's whole point. Closed
  // ones stay available below rather than being hidden, since a rep often
  // wants the one that just closed.
  const open = rows
    .filter((r) => r.close_date && daysUntil(r.close_date) >= 0)
    .sort((a, b) => new Date(a.close_date) - new Date(b.close_date));
  const closed = rows
    .filter((r) => !r.close_date || daysUntil(r.close_date) < 0)
    .slice(0, CLOSING_LIMIT);

  if (rows.length === 0) {
    return (
      <p className="accounts-panel-note">
        No Cal eProcure solicitations recorded for this agency.
      </p>
    );
  }

  const Row = ({ r }) => {
    const d = daysUntil(r.close_date);
    const urgency = d != null && d >= 0
      ? (d <= 7 ? ' accounts-due-urgent' : d <= 30 ? ' accounts-due-soon' : '')
      : '';
    return (
      <div className="accounts-sol-row">
        <div className="accounts-sol-head">
          <Link to={`/solicitations/${encodeURIComponent(r.solicitation_id)}`}>
            {r.title || '(untitled)'}
          </Link>
          <span className={`accounts-sol-due${urgency}`}>
            {r.close_date ? fmtDay(r.close_date) : 'no close date'}
            {d != null && d >= 0 && ` · ${d}d`}
          </span>
        </div>
        <span className="accounts-sol-meta">
          <span>{r.solicitation_id}</span>
          {r.status_raw && <span className="accounts-chip">{r.status_raw}</span>}
          {Number(r.sku_count) > 0 && <span>{r.sku_count} SKUs</span>}
          {Number(r.attachment_count) > 0 && <span>{r.attachment_count} attachments</span>}
          {/* The scraped name, shown when it differs from the account — this is
              how a rep sees that a row arrived via the alias rather than a
              resolved id. */}
          {r.department_name_raw && <span className="accounts-sol-raw">{r.department_name_raw}</span>}
        </span>
      </div>
    );
  };

  return (
    <div className="accounts-solicitations">
      <div className="ts-card">
        <h3 className="ts-card-title">
          Closing soon<span className="accounts-eyebrow">{open.length} open</span>
        </h3>
        {open.length === 0
          ? <p className="accounts-panel-note">Nothing open right now.</p>
          : open.slice(0, CLOSING_LIMIT).map((r) => <Row r={r} key={r.solicitation_id} />)}
      </div>

      {closed.length > 0 && (
        <div className="ts-card accounts-panel-solo">
          <h3 className="ts-card-title">
            Recently closed<span className="accounts-eyebrow">{rows.length - open.length} total</span>
          </h3>
          {closed.map((r) => <Row r={r} key={r.solicitation_id} />)}
        </div>
      )}
    </div>
  );
}
